<script setup lang="ts">
import type { InterfaceUser } from "~/classes/users/InterfaceUser"

import { PropertyStatus } from "~/classes/entity"
import { FileUserRole, FileUserRoleTier } from "~/classes/file/properties/FileUserRole"

const { state: sharedFolderState, inviteUserToCurrentSharedFolder, currentSharedFolderRole, editCurrentSharedFolderMemberPermissions } = useSharedFolders()
const { currentSharedFolderHome } = useNavigation()
const { state: savedUserState } = useSavedUsers()
const { state: myself } = useSelf()

const userDetails = computed(() => {
  const roles: Record<string, {
    userId: string
    role: FileUserRole
    status: PropertyStatus
    user?: InterfaceUser
    deleted?: boolean
  }> = currentSharedFolderHome.value?.sharedWithUserRoles() ?? {}
  for (const userId in roles)
    if (!roles[userId].deleted)
      roles[userId].user = savedUserState.value.publicUserInfo[userId]

  return roles
})

function allowRoleEditing({ user, role, status }: { user?: InterfaceUser, role: FileUserRole, status: PropertyStatus }) {
  if (status !== PropertyStatus.StatusCurrent) // still pending
    return false
  if (myself.value.user?.userId === user?.userId) // cannot edit own role
    return false
  if (!isDefined(currentSharedFolderRole.value)) // if we cannot find your role
    return false
  switch (role.tier()) {
    case FileUserRoleTier.Owner:
      return currentSharedFolderRole.value.canRemoveOwners()
    case FileUserRoleTier.Admin:
      return currentSharedFolderRole.value.canRemoveAdministrators()
    case FileUserRoleTier.Editor:
      return currentSharedFolderRole.value.canRemoveEditors()
    case FileUserRoleTier.Viewer:
      return currentSharedFolderRole.value.canRemoveViewers()
    default:
      return false
  }
}

function allowChangeToRole({ user, newRole, role, status }: {
  user?: InterfaceUser
  role: FileUserRole
  newRole: FileUserRole
  status: PropertyStatus
}) {
  if (status !== PropertyStatus.StatusCurrent) // still pending
    return false
  if (myself.value.user?.userId === user?.userId) // cannot edit own role
    return false
  if (!isDefined(currentSharedFolderRole.value)) // if we cannot find your role
    return false
  if (newRole.isBase()) // need to check if we can remove the current role
    switch (role.tier()) {
      case FileUserRoleTier.Owner:
        return currentSharedFolderRole.value.canRemoveOwners()
      case FileUserRoleTier.Admin:
        return currentSharedFolderRole.value.canRemoveAdministrators()
      case FileUserRoleTier.Editor:
        return currentSharedFolderRole.value.canRemoveEditors()
      case FileUserRoleTier.Viewer:
        return currentSharedFolderRole.value.canRemoveViewers()
      default:
        return false
    }
  switch (newRole.tier()) {
    case FileUserRoleTier.Owner:
      return currentSharedFolderRole.value.canAddOwners()
    case FileUserRoleTier.Admin:
      return currentSharedFolderRole.value.canAddAdministrators()
    case FileUserRoleTier.Editor:
      return currentSharedFolderRole.value.canAddEditors()
    case FileUserRoleTier.Viewer:
      return currentSharedFolderRole.value.canAddViewers()
    default:
      return false
  }
}

function updateCurrentSharedFolderMemberPermissions({ user, role, newRole, status }: {
  user?: InterfaceUser
  role: FileUserRole
  newRole: FileUserRole
  status: PropertyStatus
}) {
  if (!isDefined(currentSharedFolderHome.value) || !isDefined(user))
    return console.error("No shared folder or user?")
  if (!allowChangeToRole({ user, role, newRole, status }))
    return console.error("Cannot edit role", { user, role, newRole, status })
  editCurrentSharedFolderMemberPermissions({
    userId: user.userId,
    role: newRole,
    remove: newRole.isBase(),
  })
}
</script>

<template>
  <dropdown-menu class="sharing-dropdown-menu">
    <menu-section>
      <menu-header label="Access" />
      <div class="menu-sharing-access">
        <div class="inner">
          <div class="icon">
            <icon-action
              size="xs"
              type="check"
            />
          </div>
          <div>Private</div>
          <icon-property
            size="xs"
            type="private"
            class="ml-auto"
          />
          <div>Invite only</div>
        </div>
      </div>
      <div class="menu-sharing-access opacity-25">
        <div class="inner">
          <div class="icon invisible">
            <icon-action
              size="xs"
              type="check"
            />
          </div>
          <div class="">
            Public
          </div>
          <icon-property
            size="xs"
            type="public"
            class="ml-auto"
          />
          <div>Live</div>
        </div>
      </div>
    </menu-section>
    <menu-separator />
    <menu-section>
      <menu-header label="Permissions" />
      <div class="menu-permissions">
        <interface-input
          v-model="sharedFolderState.current!.invite.email"
          class="menu-permissions-input"
          placeholder="Invite by email..."
          @keydown.enter="inviteUserToCurrentSharedFolder()"
        />
        <interface-button
          class="menu-permissions-button"
          :loading="sharedFolderState.current!.invite.isLoading"
          @click="inviteUserToCurrentSharedFolder()"
        >
          Invite
        </interface-button>
      </div>
      <div
        v-for="{ user, role, status }, key in userDetails"
        :key
        class="menu-user"
      >
        <user-avatar :user :size="28" />
        <div class="menu-user-details">
          <div v-if="user?.handle()" class="menu-user-name no-decoration">
            @{{ user?.handle()?.handle }}
          </div>
          <div v-else class="menu-user-name">
            Loading...
          </div>
          <div class="menu-user-email">
            {{ user?.email() ?? "Loading..." }}
          </div>
        </div>
        <!-- TODO: writable permission object -->
        <menu-selection
          :model-value="role"
          :label="k => k.isBase() ? 'None (remove)' : k.tierName()"
          :disabled="!allowRoleEditing({ user, role, status })"
          :loading="enumEq(status, PropertyStatus.StatusPending)"
          :options="[
            FileUserRole.newOwner(),
            FileUserRole.newAdmin(),
            FileUserRole.newEditor(),
            FileUserRole.newViewer(),
            FileUserRole.newBase(),
          ]"
          :disable-option="newRole => !allowChangeToRole({ user, role, newRole, status })"
          @update:model-value="newRole => updateCurrentSharedFolderMemberPermissions({ user, role, newRole, status })"
        />
      </div>
    </menu-section>
  </dropdown-menu>
</template>

<style lang="sass" scoped>
@import '~/assets/styles/generated/variables.sass'

.sharing-dropdown-menu
  @apply w-[#{$menu-share-w-default}]

.menu-sharing-access
  @apply h-[#{$menu-share-h-item}] t-menu-base
  .inner
    @apply -m-1 p-1 rounded-1 flex gap-[#{$menu-share-gap-item}] items-center cursor-pointer
    &:hover,&.selected
      @apply bg-[#{$menu-bg-item-hover}]
  .icon
    @apply h-[#{$global-w-icon-xs}] w-[#{$global-w-icon-xs}]

.menu-permissions
  @apply flex gap-[#{$menu-gap-items}]
.menu-permissions-input
  @apply flex-grow-1 w-full
.menu-permissions-button
  @apply bg-$global-blue-primary px-2.5
  &:hover
    @apply bg-$global-blue-primary

.menu-user
  @apply flex items-center gap-[#{$menu-gap-items}]
.menu-user-avatar
  @apply rounded-full bg-red-400 w-[#{$menu-share-h-avatar}] h-[#{$menu-share-h-avatar}]
.menu-user-details
  @apply flex-grow-1
  .menu-user-name
    @apply t-menu-base
  .menu-user-email
    @apply t-menu-xs text-[#{$menu-share-t-permissions-secondary}]
.menu-user-role
  @apply ml-auto
</style>
